import { useState } from 'react';
import { Icon } from '@gftg/ui';
import { Link } from 'react-router';
import { Avatar } from '../components/Avatar';
import { CategoryChip, CertificationChip } from '../components/Chips';
import { FilterChip } from '../components/FilterChip';
import { FilterSheet } from '../components/FilterSheet';
import { EmptyBlock, ErrorBlock, LoadingBlock, PageHeader } from '../components/states';
import { formatLongMonthDay } from '../lib/datetime';
import { roleLine } from '../lib/people';
import {
  useDirectory,
  useEvent,
  type Directory,
  type DirectoryOption,
  type DirectoryPerson,
} from '../lib/queries';
import { useDebounced } from '../lib/useDebounced';

type FilterKey = 'categoryId' | 'industryId' | 'certificationId' | 'interestId';

type Selection = Partial<Record<FilterKey, string>>;

interface FilterGroup {
  key: FilterKey;
  /** The heading in the sheet, and the word the removable chip in the header is read as. */
  title: string;
  /** The "no choice" chip, phrased for the group rather than a uniform "Any". */
  anyLabel: string;
  options: (directory: Directory) => DirectoryOption[];
}

/**
 * All four vocabularies DIR-02 names, in the order the sheet asks them in: who somebody is, then
 * what they do, then what they hold, then what they came to talk about. Each list is the API's,
 * computed over the people actually listed, so no chip here is a dead end.
 */
const GROUPS: FilterGroup[] = [
  {
    key: 'categoryId',
    title: 'Who they are',
    anyLabel: 'Anyone',
    options: (directory) => directory.categories,
  },
  {
    key: 'industryId',
    title: 'Industry',
    anyLabel: 'Any industry',
    options: (directory) => directory.industries,
  },
  {
    key: 'certificationId',
    title: 'Certification',
    anyLabel: 'Any certification',
    options: (directory) => directory.certifications,
  },
  {
    key: 'interestId',
    title: 'Interested in',
    anyLabel: 'Any topic',
    options: (directory) => directory.interests,
  },
];

/**
 * Who is coming (module 6). The filters are the ones these attendees actually use to find each
 * other — category, industry, certification and interest (DIR-02) — and the card carries nothing
 * more than DIR-01 asks for: no email, no phone, at any setting (PRF-05).
 *
 * Four filters is more than a phone header can hold, so they live in a sheet: what stays on screen
 * is the search box, a button that says how many are set, and a removable chip for each one, which
 * is the part a filter hidden behind a button otherwise loses.
 */
export function DirectoryPage() {
  const event = useEvent();
  const [search, setSearch] = useState('');
  const [selection, setSelection] = useState<Selection>({});
  const [sheetOpen, setSheetOpen] = useState(false);
  const q = useDebounced(search, 250);

  const directory = useDirectory({ q, ...selection });
  const pages = directory.data?.pages ?? [];
  const first = pages.at(0);
  const people = pages.flatMap((page) => page.people);
  const active = GROUPS.filter((group) => selection[group.key]);
  const filtered = Boolean(q) || active.length > 0;

  const toggle = (key: FilterKey, id: string) =>
    setSelection((current) => ({ ...current, [key]: current[key] === id ? undefined : id }));

  return (
    <>
      {/* The search box and what is filtered stay put while the list moves under them: on a list of
          a few hundred people, scrolling back up to change a filter is the whole interaction. */}
      <div className="sticky top-0 z-10 border-b border-border-2 bg-surface lg:top-20">
        <PageHeader title="Who's coming" />

        {/* Stacked on a phone, because the search box needs the whole width there. On a laptop the
            box, the button and the chips fit on one line, and stacking them would push the first
            person off the fold for no reason. */}
        <div className="px-gutter space-y-2.5 pb-3 lg:flex lg:items-center lg:gap-3 lg:space-y-0">
          <input
            type="search"
            value={search}
            onChange={(item) => setSearch(item.target.value)}
            placeholder="Search name, company, title or industry"
            aria-label="Search the directory"
            className="text-body rounded-button min-h-touch w-full border border-border-input bg-surface px-4 text-ink placeholder:text-ink-4 lg:w-80 lg:shrink-0"
          />

          <div className="flex min-w-0 items-center gap-2 lg:flex-1">
            <button
              type="button"
              onClick={() => setSheetOpen(true)}
              aria-haspopup="dialog"
              aria-expanded={sheetOpen}
              // The chips inside the sheet are the API's vocabularies, so until the first page
              // arrives there is nothing to open: a button that opens an empty sheet is worse
              // than one that visibly waits.
              disabled={!first}
              className={`text-chip min-h-tap rounded-button flex shrink-0 items-center gap-1.5 px-3 font-semibold disabled:text-ink-4 ${
                active.length > 0
                  ? 'bg-ink text-white'
                  : 'border border-border-input bg-surface text-ink-2'
              }`}
            >
              {/* The kit's sliders glyph. It is named for the console's own screen, and a set of
                  sliders is what it draws. */}
              <Icon name="settings" size={16} />
              Filters
              {active.length > 0 && (
                <span className="text-eyebrow flex size-4 items-center justify-center rounded-full bg-white font-bold text-ink tabular-nums">
                  {active.length}
                </span>
              )}
            </button>

            {/* Set filters are named on the screen rather than only counted behind the button: a
                list that has quietly hidden three hundred people has to say so where the people
                are missing from. */}
            {first && active.length > 0 && (
              <div className="-mr-gutter pr-gutter flex flex-1 gap-2 overflow-x-auto py-0.5">
                {active.map((group) => (
                  <ActiveFilterChip
                    key={group.key}
                    label={labelOf(group, first, selection[group.key])}
                    onRemove={() =>
                      setSelection((current) => ({ ...current, [group.key]: undefined }))
                    }
                  />
                ))}
                <button
                  type="button"
                  onClick={() => setSelection({})}
                  className="text-chip min-h-tap shrink-0 px-1 font-semibold text-brand-text underline"
                >
                  Clear all
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {first && (
        <FilterSheet
          open={sheetOpen}
          title="Filters"
          onClose={() => setSheetOpen(false)}
          footer={
            <button
              type="button"
              onClick={() => setSheetOpen(false)}
              className="text-button rounded-button min-h-touch w-full bg-ink font-semibold text-white"
            >
              Show {countLabel(first.matched)}
            </button>
          }
        >
          <div className="space-y-5">
            {GROUPS.map((group) => {
              const options = group.options(first);
              if (options.length === 0) {
                return null;
              }

              return (
                <fieldset key={group.key}>
                  <legend className="text-eyebrow tracking-eyebrow font-bold text-ink-3 uppercase">
                    {group.title}
                  </legend>
                  <div className="mt-2.5 flex flex-wrap gap-2">
                    <FilterChip
                      active={!selection[group.key]}
                      onClick={() =>
                        setSelection((current) => ({ ...current, [group.key]: undefined }))
                      }
                    >
                      {group.anyLabel}
                    </FilterChip>
                    {options.map((option) => (
                      <FilterChip
                        key={option.id}
                        active={option.id === selection[group.key]}
                        onClick={() => toggle(group.key, option.id)}
                      >
                        {option.label}
                      </FilterChip>
                    ))}
                  </div>
                </fieldset>
              );
            })}
          </div>
        </FilterSheet>
      )}

      <main className="px-gutter pt-3 pb-6">
        {directory.isPending && <LoadingBlock label="Loading the directory…" />}

        {directory.isError && (
          <ErrorBlock
            message="The directory could not be loaded."
            onRetry={() => void directory.refetch()}
          />
        )}

        {first && (
          <>
            {/* People, never tickets (DATA-01) — and the second number says plainly that the
                list is not everybody yet, which is the question this screen provokes. */}
            <p className="text-subtitle text-ink-3">
              {countLabel(first.matched)} · {first.registered} registered so far
            </p>

            {people.length === 0 && filtered && (
              <EmptyBlock title="Nothing matches that">
                {active.length > 0
                  ? 'Try a different name, or clear a filter or two.'
                  : 'Try a different name, company or industry.'}
              </EmptyBlock>
            )}

            {people.length === 0 && !filtered && (
              <EmptyBlock title="Nobody is listed yet">
                The directory fills up as people register and fill in their profiles.
              </EmptyBlock>
            )}

            {/* Two to a row on a laptop. A directory of a few hundred people is the screen that
                gains most from the width: one column of full-width cards is half a screen of
                cards and half a screen of nothing. */}
            <div className="space-y-card mt-3 lg:grid lg:grid-cols-2 lg:gap-card lg:space-y-0">
              {people.map((person) => (
                <PersonCard key={person.id} person={person} />
              ))}
            </div>

            {directory.hasNextPage && (
              <button
                type="button"
                onClick={() => void directory.fetchNextPage()}
                disabled={directory.isFetchingNextPage}
                className="text-row rounded-button min-h-touch mt-4 w-full border border-border-input bg-surface font-semibold text-ink disabled:text-ink-4 lg:mx-auto lg:block lg:w-auto lg:px-10"
              >
                {directory.isFetchingNextPage ? 'Loading…' : 'Show more'}
              </button>
            )}

            {first.registrationClosesOn && event.data && (
              <p className="text-subtitle mt-5 text-ink-4">
                Registration runs until{' '}
                {formatLongMonthDay(first.registrationClosesOn, event.data.timezone)}, so this list
                keeps growing.
              </p>
            )}
          </>
        )}
      </main>
    </>
  );
}

function PersonCard({ person }: { person: DirectoryPerson }) {
  // DATA-06: most of these arrive blank for somebody, and a card of empty lines is not an error.
  const role = roleLine(person.jobTitle, person.company);

  return (
    // The card is the way to the profile (DIR-05). The mockup draws no chevron, and it does not
    // need one: on a list where every row opens, the affordance is the card.
    <Link
      to={`/directory/${person.id}`}
      className="rounded-card flex gap-3 border border-border bg-surface p-4"
    >
      <Avatar name={person.name} photoAssetId={person.photoAssetId} />

      <div className="min-w-0 flex-1">
        <h2 className="text-row font-semibold text-ink">{person.name}</h2>
        {role && <p className="text-subtitle mt-0.5 text-ink-3">{role}</p>}
        {person.industry && <p className="text-subtitle text-ink-3">{person.industry}</p>}

        <div className="mt-2 flex flex-wrap gap-1.5">
          <CategoryChip label={person.category} />
          {person.certifications.map((certification) => (
            <CertificationChip key={certification.id} label={certification.label} />
          ))}
        </div>

        {/* DIR-04: they are here on their registration answers, which is why there is no
            industry. */}
        {!person.hasProfile && (
          <p className="text-eyebrow mt-2 text-ink-4">Profile not filled in yet</p>
        )}
      </div>
    </Link>
  );
}

function countLabel(matched: number): string {
  return matched === 1 ? '1 attendee' : `${matched} attendees`;
}

/**
 * One filter, named and removable. The × is on the chip rather than in the sheet because undoing
 * one choice is the common move, and reopening the sheet to make it is three taps for one.
 */
function ActiveFilterChip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <span className="text-chip min-h-tap flex shrink-0 items-center gap-1.5 rounded-full border border-border-input bg-sand-2 pr-1 pl-3 font-semibold text-ink-2">
      {label}
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove the ${label} filter`}
        className="flex size-7 items-center justify-center rounded-full text-ink-3"
      >
        {/* A multiplication sign rather than the letter: the kit's icon set has no × in it, and
            this is the glyph it is drawn from. */}
        <span aria-hidden>×</span>
      </button>
    </span>
  );
}

/**
 * The chosen option's own label, which only the API's vocabulary knows. It falls back to the
 * group's name for the moment between a choice and the response that explains it — a page that
 * was fetched before the filter was set has no row for it yet.
 */
function labelOf(group: FilterGroup, directory: Directory, id: string | undefined): string {
  const option = group.options(directory).find((candidate) => candidate.id === id);
  return option?.label ?? group.title;
}
